import { yupResolver } from '@hookform/resolvers/yup'
import { find, findIndex, map } from 'lodash'
import { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import {
  Button,
  Card,
  Checkbox,
  DateTimePicker,
  Input,
  Modal,
  Textarea,
} from '@/components/ui'
import SelectField from '@/components/select-field'
import { Extras, ExtrasDetails, Languages } from '../../../common/models'
import { Country } from '../../../common/models/locale'
import FormGroup from '../../../core/components/FormGroup'
import {
  createExtra,
  deleteExtra,
  getAllCountries,
  getAllExtras,
  updateExtra,
} from '../../../core/services/extras'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Icon } from '@/common/components/Icon'

interface IValues {
  published?: boolean
  requiresCheckin?: boolean
  startTimestamp?: string
  endTimestamp?: string
  countryDependentPrice?: boolean
  deliverable?: boolean
  inWalletBuy?: boolean
  defaultPrice?: number
  translations?: IAddonContent[]
  countryPrices?: CountryContent[] | null
}

interface IAddonPayload {
  id?: string
  translations?: IAddonContent[]
  published?: boolean
  requiresCheckin?: boolean
  countryPrices?: CountryContent[] | null
  startTimestamp?: number
  defaultPrice?: number
  endTimestamp?: number
  countryDependentPrice?: boolean
  deliverable?: boolean
  inWalletBuy?: boolean
}

interface IAddonContent {
  locale: string
  name: string
  description: string
}

interface CountryContent {
  countryId: string
  countryName: string
  price: number
}

export interface CreateAddonModalProps {
  showCreateModal: boolean
  addon?: ExtrasDetails
  setShowCreateModal: (show: boolean) => void
  setAddons: (addon: Extras[]) => void
  editMode: boolean
  setTotalElemenets: (e: number) => void
  isActive: boolean
  page: number
  size: number
  currency: string
  allLanguages: Languages[]
}

export const CreateAddonModal = (props: CreateAddonModalProps): JSX.Element => {
  const {
    showCreateModal,
    page,
    size,
    setShowCreateModal,
    addon,
    editMode,
    setAddons,
    setTotalElemenets,
    isActive,
    currency,
    allLanguages,
  } = props

  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const [deleteLocationContent, setDeleteLocationContent] =
    useState<boolean>(false)
  const [removeLocationContent, setRemoveLocationContent] = useState<number>(0)
  const [countries, setCountries] = useState<Country[]>([])
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const response = await getAllCountries()
        setCountries(response.data)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [])

  const handleClose = () => {
    setShowCreateModal(false)
  }

  const deleteLocation = () => {
    locationRemove(removeLocationContent)
  }

  const handleDelete = () => {
    setDeleteModal(true)
  }

  const schema = yup.object().shape({
    startTimestamp: yup.string(),
    endTimestamp: yup.string(),
    published: yup.boolean(),
    deliverable: yup.boolean(),
    inWalletBuy: yup.boolean(),
    defaultPrice: yup.number(),
    requiresCheckin: yup.boolean(),
    countryDependentPrice: yup.boolean(),
    translations: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          locale: yup.string().required(),
          name: yup.string().required(),
          description: yup.string().required(),
        }),
      )
      .required('This field is required.'),
    countryPrices: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          countryId: yup.string().required(),
          countryName: yup.string().required(),
          price: yup.number().required(),
        }),
      )
      .nullable(),
  })

  const {
    handleSubmit,
    control,
    register,
    setValue,
    reset,
    formState: { errors, dirtyFields },
    watch,
  } = useForm<IValues>({
    mode: 'onChange',
    resolver: yupResolver<IValues>(schema),
    defaultValues: {
      startTimestamp: new Date().toISOString(),
      endTimestamp: new Date().toISOString(),
      published: false,
      deliverable: false,
      requiresCheckin: false,
      inWalletBuy: false,
      countryDependentPrice: false,
      defaultPrice: 0,
      countryPrices: [
        {
          countryId: 'all_locations_all_locations_all_',
          countryName: 'all_locations',
          price: 0,
        },
      ],
    },
  })

  const deliverableWatch = watch('deliverable')

  const {
    fields: locationFields,
    append: locationAppend,
    remove: locationRemove,
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'countryPrices', // unique name for your Field Array
  })

  const location = watch('countryPrices')

  const submit = async (values: IValues) => {
    let countryPrice = values.countryPrices
    let countryDepPrice = true
    let defaultPrice = addon?.defaultPrice || 0

    if (
      values?.countryPrices?.length === 0 ||
      values?.countryPrices?.[0]?.countryName === 'all_locations'
    ) {
      defaultPrice =
        defaultPrice !== 0 ? defaultPrice : values.countryPrices[0].price
      countryPrice = null
      countryDepPrice = false
    }
    const data: IAddonPayload = {
      id: addon?.id,
      published: values.published,
      requiresCheckin: values.requiresCheckin,
      startTimestamp: new Date(values.startTimestamp || '').valueOf(),
      endTimestamp: new Date(values.endTimestamp || '').valueOf(),
      translations: values.translations,
      deliverable: values.deliverable,
      inWalletBuy: values.inWalletBuy,
      countryPrices: countryPrice,
      countryDependentPrice: countryDepPrice,
      defaultPrice: defaultPrice,
    }
    if (addon?.id) {
      try {
        const { status } = await updateExtra(addon.id, data)
        const response = await getAllExtras({ page: page - 1, size })
        setAddons(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
    } else {
      try {
        const { status } = await createExtra(data)
        const response = await getAllExtras({ page: page - 1, size })
        setAddons(response.data.content)
        setTotalElemenets(response.data.totalElements)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
    }
    handleClose()
  }

  const deleteAddon = () => {
    // prettier-ignore
    (async () => {
      try {
        if (addon) {
          const { status } = await deleteExtra([addon?.id])
          const response = await getAllExtras({ page: page - 1, size })
          setAddons(response.data.content)
          setTotalElemenets(response.data.totalElements)
          if (status === 204 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  useEffect(() => {
    if (deliverableWatch) {
      setValue('requiresCheckin', true)
    }
  }, [deliverableWatch])

  useEffect(() => {
    const defaultLocationContent = [
      {
        countryId: 'all_locations_all_locations_all_',
        countryName: 'all_locations',
        price: 0,
      },
    ]
    const temp = map(allLanguages, (lang) => {
      return {
        locale: lang.locale,
        name: '',
        description: '',
      }
    })
    setValue('translations', temp)
    if (addon) {
      setTimeout(() => {
        setValue(
          'countryPrices',
          addon?.countryPrices?.length !== 0
            ? addon?.countryPrices
            : defaultLocationContent,
        )
      }, 100)
      if (!!addon.translations && addon.translations.length > 0) {
        setTimeout(() => {
          map(addon.translations, (trans) => {
            const index = findIndex(
              allLanguages,
              (l) => l.locale === trans.locale,
            )
            if (index !== -1) {
              setValue(`translations.${index}.description`, trans.description)
              setValue(`translations.${index}.locale`, trans.locale)
              setValue(`translations.${index}.name`, trans.name)
            }
          })
        }, 100)
      }

      setValue('startTimestamp', new Date(addon?.startTimestamp).toISOString())
      setValue('endTimestamp', new Date(addon?.endTimestamp).toISOString())
      setValue('published', addon.published)
      setValue('requiresCheckin', addon.requiresCheckin)
      setValue('deliverable', addon.deliverable)
      setValue('inWalletBuy', addon.inWalletBuy)
      setValue('defaultPrice', addon.defaultPrice)

      setValue('countryDependentPrice', addon.countryDependentPrice)
    }
    return () => {
      reset()
    }
  }, [addon, showCreateModal])

  const isReadOnly = !isActive && editMode
  const modalSubtitle = editMode
    ? isReadOnly
      ? 'View Add-on'
      : 'Edit Add-on'
    : 'Add New Add-on'

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <div className="mb-5 text-2xl font-bold leading-none text-monochrome-1">
      {children}
    </div>
  )

  return (
    <>
      <Modal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Add-ons" subtitle={modalSubtitle} />
          </div>
        }
        footer={
          editMode && isReadOnly ? (
            <Button onClick={handleClose} type="button" variant="secondary">
              Close
            </Button>
          ) : (
            <div className="flex w-full items-center gap-2">
              {editMode && (
                <Button
                  onClick={handleDelete}
                  variant="destructive"
                  className="mr-auto"
                  type="button"
                >
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              )}
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                form="tickets-addon-form"
                type="submit"
                variant="primary"
                disabled={Object.keys(dirtyFields).length === 0}
              >
                {addon?.id ? 'Save' : 'Create'}
              </Button>
            </div>
          )
        }
      >
        <SectionTitle>Main Info</SectionTitle>
        <form
          id="tickets-addon-form"
          noValidate
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <div className="flex flex-col gap-2">
            {map(allLanguages, (lang, index) => (
              <Card
                key={lang.id}
                className="flex flex-col gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4"
              >
                <FormGroup label="Language:" wrapperClassName="mb-0">
                  <span className="text-sm font-semibold text-accent-1">
                    {lang.name}
                  </span>
                </FormGroup>
                <FormGroup
                  label="Title"
                  error={errors?.translations?.[index]?.name?.message}
                >
                  <Input
                    isInvalid={!!errors?.translations?.[index]?.name?.message}
                    {...register(`translations.${index}.name`)}
                    type="text"
                    placeholder="Enter Title"
                    disabled={isReadOnly}
                  />
                </FormGroup>
                <FormGroup
                  label="Add-on description"
                  error={errors?.translations?.[index]?.description?.message}
                >
                  <Textarea
                    isInvalid={
                      !!errors?.translations?.[index]?.description?.message
                    }
                    {...register(`translations.${index}.description`)}
                    rows={5}
                    placeholder="Enter Description"
                    disabled={isReadOnly}
                  />
                </FormGroup>
              </Card>
            ))}
          </div>

          <div className="my-4 h-px w-full bg-monochrome-120" />

          <SectionTitle>Locations</SectionTitle>
          <div className="space-y-7">
            {location?.length === 0 ||
            location?.[0]?.countryName === 'all_locations' ? (
              <Card className="flex flex-col gap-4 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4">
                <Controller
                  name="countryPrices.0.countryName"
                  control={control}
                  render={({ field }) => (
                    <SelectField
                      outline
                      label="Location (default settings) - optional"
                      options={[
                        {
                          value: 'all_locations',
                          label: 'All Locations',
                        },
                        ...map(countries, (country) => ({
                          value: country.name,
                          label: country.name,
                        })),
                      ]}
                      value={field.value || 'all_locations'}
                      onValueChange={(value) => {
                        field.onChange(value)
                        setValue(
                          'countryPrices',
                          [
                            {
                              countryId:
                                find(
                                  countries,
                                  (country) => country.name === value,
                                )?.id || 'all_locations_all_locations_all_',
                              countryName: value,
                              price: location?.[0]?.price || 0,
                            },
                          ],
                          {
                            shouldValidate: true,
                          },
                        )
                      }}
                      disabled
                      error={errors?.countryPrices?.[0]?.countryName?.message}
                    />
                  )}
                />
                <FormGroup
                  label="Addons Price (default settings)"
                  error={errors?.countryPrices?.[0]?.price?.message}
                >
                  <Input
                    isInvalid={!!errors?.countryPrices?.[0]?.price?.message}
                    {...register('countryPrices.0.price')}
                    type="text"
                    placeholder={`Enter Addon Price in ${currency}`}
                    disabled={isReadOnly}
                  />
                </FormGroup>
              </Card>
            ) : (
              <>
                {map(locationFields, (field, index) => (
                  <Card
                    key={field.id}
                    className="flex flex-col gap-4 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50 p-4"
                  >
                    <Controller
                      name={`countryPrices.${index}.countryName`}
                      control={control}
                      render={({ field: countryField }) => (
                        <SelectField
                          outline
                          label="Location (default settings) - optional"
                          options={[
                            { value: '000', label: 'All Locations' },
                            ...map(countries, (country) => ({
                              value: country.name,
                              label: country.name,
                            })),
                          ]}
                          value={countryField.value || '000'}
                          onValueChange={(value) => {
                            countryField.onChange(value)
                            const id = find(
                              countries,
                              (c) => c.name === value,
                            )?.id
                            if (id) {
                              setValue(`countryPrices.${index}.countryId`, id)
                            }
                          }}
                          disabled={isReadOnly}
                          error={
                            errors?.countryPrices?.[index]?.countryName?.message
                          }
                        />
                      )}
                    />
                    <FormGroup
                      label="Addon Price (default settings)"
                      error={errors?.countryPrices?.[index]?.price?.message}
                    >
                      <Input
                        isInvalid={
                          !!errors?.countryPrices?.[index]?.price?.message
                        }
                        {...register(`countryPrices.${index}.price`)}
                        disabled={isReadOnly}
                        placeholder={`Enter Addon Price in ${currency}`}
                      />
                    </FormGroup>
                    <Button
                      onClick={() => {
                        setDeleteLocationContent(true)
                        setRemoveLocationContent(index)
                      }}
                      variant="destructive"
                      className="w-full"
                      type="button"
                      disabled={isReadOnly}
                    >
                      Delete
                    </Button>
                  </Card>
                ))}
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() =>
                    locationAppend({
                      countryId: '',
                      countryName: 'all_locations',
                      price: 0,
                    })
                  }
                  type="button"
                  disabled={isReadOnly}
                >
                  Add new
                </Button>
              </>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Controller
              name="startTimestamp"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="Start Date (default settings)"
                  error={errors?.startTimestamp?.message}
                >
                  <DateTimePicker
                    placeholder="Select start date"
                    hasError={!!errors?.startTimestamp?.message}
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) =>
                      field.onChange(date ? date.toISOString() : undefined)
                    }
                    disabled={isReadOnly}
                  />
                </FormGroup>
              )}
            />
            <Controller
              name="endTimestamp"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="End Date (default settings)"
                  error={errors?.endTimestamp?.message}
                >
                  <DateTimePicker
                    placeholder="Select end date"
                    hasError={!!errors?.endTimestamp?.message}
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) =>
                      field.onChange(date ? date.toISOString() : undefined)
                    }
                    disabled={isReadOnly}
                  />
                </FormGroup>
              )}
            />
          </div>

          <div className="flex flex-col gap-3">
            <Controller
              name="deliverable"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="Deliverable"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                >
                  <Checkbox
                    className="border-monochrome-1"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={isReadOnly}
                  />
                </FormGroup>
              )}
            />
            <Controller
              name="inWalletBuy"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="Show In Wallet"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                >
                  <Checkbox
                    className="border-monochrome-1"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={isReadOnly}
                  />
                </FormGroup>
              )}
            />
            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="Published"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                >
                  <Checkbox
                    className="border-monochrome-1"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={isReadOnly}
                  />
                </FormGroup>
              )}
            />
            <Controller
              name="requiresCheckin"
              control={control}
              render={({ field }) => (
                <FormGroup
                  label="Requires Checkin"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                >
                  <Checkbox
                    className="border-monochrome-1"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={isReadOnly || deliverableWatch}
                  />
                </FormGroup>
              )}
            />
          </div>
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteModal}
        setDeleteConfirmationModal={setDeleteModal}
        deleteTitle={'Add-on'}
        deleteFunc={deleteAddon}
        deletedItem={addon?.translations?.[0]?.name}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteLocationContent}
        setDeleteConfirmationModal={setDeleteLocationContent}
        deleteTitle={'Location'}
        deleteFunc={deleteLocation}
        deletedItem={locationFields[removeLocationContent]?.countryName}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
